import React, { useState, useMemo, Fragment } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Building2, Search, Pencil, Save, X, Plus, Download, ExternalLink,
  ChevronDown, ChevronRight, AlertCircle,
} from 'lucide-react';
import { exportToCSV, exportToExcel } from '@/lib/fin/export';
import { Skeleton } from '@/components/ui/skeleton';

/* ───── Types ─────────────────────────────────────────────────────────── */

interface Entity {
  id: string;
  name: string;
  legal_name: string | null;
  jurisdiction: string | null;
  regulator: string | null;
  licence_type: string | null;
  parent_entity: string | null;
  parent_name: string | null;
  group_tag: string | null;
  is_active: boolean;
  min_capital_eur: number | string | null;
  created_at?: string;
  updated_at?: string;
}

interface EntitiesProps {
  onNavigate?: (page: string, params?: Record<string, string>) => void;
}

/* ───── Group label helpers ───────────────────────────────────────────── */

// DB stores group_tag as lowercase ('wwg' / 'vault'); the UI used to
// display "WWG Group" / "The Vault Group". Keep the same mapping for
// continuity but fall back to the raw value for any new groups.
const GROUP_LABELS: Record<string, string> = {
  wwg: 'WWG Group',
  vault: 'The Vault Group',
  tff: 'Together Forever Foundation',
};
function groupLabel(tag: string | null): string {
  if (!tag) return 'Other';
  return GROUP_LABELS[tag] || tag;
}

// fmtEur provided via useCurrency — initialized inside components
function useFmtEur() {
  const { fmt } = useCurrency();
  return (v: number | string | null): string => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!isFinite(n)) return '—';
    return fmt(n, 'EUR');
  };
}

/* ───── Hierarchy builder ─────────────────────────────────────────────── */

interface TreeRow {
  entity: Entity;
  depth: number;          // 0 = root, 1+ = nested
  hasChildren: boolean;
}

/**
 * Build a flat ordered list of rows that represents the parent/child
 * hierarchy inside one group_tag slice. Any entity whose parent_entity
 * is NOT in the current slice (or is null) becomes a root; everyone
 * else nests under their parent recursively. Depth is capped at 3 for
 * sanity but the current data only goes 2 deep (wwg → pv → wwusa →
 * wwfusa is a real 3-deep chain).
 */
function buildTree(entities: Entity[], expanded: Set<string>): TreeRow[] {
  const ids = new Set(entities.map(e => e.id));
  const childrenByParent = new Map<string | null, Entity[]>();
  for (const e of entities) {
    const key = e.parent_entity && ids.has(e.parent_entity) ? e.parent_entity : null;
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key)!.push(e);
  }
  for (const list of childrenByParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const out: TreeRow[] = [];
  const visit = (parent: string | null, depth: number) => {
    const kids = childrenByParent.get(parent) || [];
    for (const e of kids) {
      const hasChildren = (childrenByParent.get(e.id) || []).length > 0;
      out.push({ entity: e, depth, hasChildren });
      if (hasChildren && expanded.has(e.id)) {
        visit(e.id, depth + 1);
      }
    }
  };
  visit(null, 0);
  return out;
}

/* ───── Main component ────────────────────────────────────────────────── */

export default function Entities({ onNavigate }: EntitiesProps) {
  const fmtEur = useFmtEur();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editEntity, setEditEntity] = useState<Entity | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: entities, loading, refresh } = useFetch<Entity[]>('/api/fin/entities');

  // Filter by search + active toggle
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (entities || []).filter(e => {
      if (!showInactive && !e.is_active) return false;
      if (!q) return true;
      return (
        e.id.toLowerCase().includes(q) ||
        e.name.toLowerCase().includes(q) ||
        (e.legal_name || '').toLowerCase().includes(q) ||
        (e.jurisdiction || '').toLowerCase().includes(q) ||
        (e.regulator || '').toLowerCase().includes(q) ||
        (e.licence_type || '').toLowerCase().includes(q)
      );
    });
  }, [entities, search, showInactive]);

  // Group by group_tag, then hierarchize within each group
  const groupedTrees = useMemo(() => {
    const byGroup = new Map<string, Entity[]>();
    for (const e of filtered) {
      const key = e.group_tag || 'other';
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(e);
    }
    // Deterministic group order: wwg first, vault second, everything else
    const groupOrder: string[] = [];
    if (byGroup.has('wwg')) groupOrder.push('wwg');
    if (byGroup.has('vault')) groupOrder.push('vault');
    for (const k of [...byGroup.keys()].sort()) {
      if (k !== 'wwg' && k !== 'vault') groupOrder.push(k);
    }
    return groupOrder.map(tag => ({
      tag,
      label: groupLabel(tag),
      rows: buildTree(byGroup.get(tag)!, expanded),
      count: byGroup.get(tag)!.length,
    }));
  }, [filtered, expanded]);

  // Default: every entity that has children is expanded on first load so
  // treasury sees the full tree. Recomputed only when entities change.
  React.useEffect(() => {
    if (!entities) return;
    const parents = new Set<string>();
    for (const e of entities) if (e.parent_entity) parents.add(e.parent_entity);
    setExpanded(parents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities?.length]);

  const toggle = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };
  const expandAll = () => {
    const all = new Set<string>();
    for (const e of entities || []) if (e.parent_entity) all.add(e.parent_entity);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  const handleExport = (format: 'csv' | 'excel') => {
    const data = (entities || []).map(e => ({
      id: e.id, name: e.name, legal_name: e.legal_name,
      jurisdiction: e.jurisdiction, regulator: e.regulator,
      licence_type: e.licence_type, parent_entity: e.parent_entity,
      group_tag: e.group_tag, is_active: e.is_active,
      min_capital_eur: e.min_capital_eur,
    }));
    const columns = [
      { key: 'id', label: 'ID' }, { key: 'name', label: 'Name' },
      { key: 'legal_name', label: 'Legal Name' }, { key: 'jurisdiction', label: 'Jurisdiction' },
      { key: 'regulator', label: 'Regulator' }, { key: 'licence_type', label: 'Licence Type' },
      { key: 'parent_entity', label: 'Parent Entity' }, { key: 'group_tag', label: 'Group' },
      { key: 'is_active', label: 'Active' }, { key: 'min_capital_eur', label: 'Min Capital EUR' },
    ];
    const fn = `entities_${new Date().toISOString().split('T')[0]}`;
    if (format === 'csv') exportToCSV(data, fn, columns);
    else exportToExcel(data, fn, columns);
  };

  if (loading && !entities) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48 mb-2" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <Building2 size={24} /> Entity Registry
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage group legal entities and their financial profiles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-2" onClick={() => setCreating(true)}>
            <Plus size={14} /> New Entity
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Download size={14} /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>Export CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')}>Export Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{entities?.length ?? 0} entities</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search id, name, jurisdiction, regulator..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={expandAll}>Expand all</Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>Collapse all</Button>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="entities-show-inactive" className="text-xs text-muted-foreground">Show inactive</Label>
          <Switch id="entities-show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
        </div>
      </div>

      {/* Hierarchical table */}
      <Card className="shadow-xs">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[30%]">Entity</TableHead>
              <TableHead>Legal Name</TableHead>
              <TableHead className="w-20">Jurisdiction</TableHead>
              <TableHead className="w-28">Regulator</TableHead>
              <TableHead className="w-28">Licence</TableHead>
              <TableHead className="text-right w-32">Min Capital</TableHead>
              <TableHead className="w-20">Status</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedTrees.map(group => (
              <Fragment key={group.tag}>
                {/* Group header row */}
                <TableRow className="bg-muted/50 hover:bg-muted/60">
                  <TableCell colSpan={8} className="py-1.5">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                      <Badge variant="outline" className="text-[10px] font-normal">{group.count}</Badge>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Tree rows */}
                {group.rows.map(({ entity: e, depth, hasChildren }) => (
                  <TableRow key={e.id} className={!e.is_active ? 'opacity-50' : ''}>
                    <TableCell className="py-2">
                      <div
                        className="flex items-center gap-1.5"
                        style={{ paddingLeft: `${depth * 18}px` }}
                      >
                        {hasChildren ? (
                          <button
                            className="p-0.5 hover:bg-muted rounded shrink-0"
                            onClick={() => toggle(e.id)}
                            aria-label={expanded.has(e.id) ? 'Collapse' : 'Expand'}
                          >
                            {expanded.has(e.id)
                              ? <ChevronDown size={12} />
                              : <ChevronRight size={12} />}
                          </button>
                        ) : (
                          <span className="w-4 shrink-0" />
                        )}
                        <span className="text-sm font-medium">{e.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      {e.legal_name && e.legal_name !== e.name ? e.legal_name : '—'}
                    </TableCell>
                    <TableCell className="text-xs py-2">{e.jurisdiction || '—'}</TableCell>
                    <TableCell className="text-xs py-2">{e.regulator || '—'}</TableCell>
                    <TableCell className="text-xs py-2">{e.licence_type || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-xs py-2">{fmtEur(e.min_capital_eur)}</TableCell>
                    <TableCell className="py-2">
                      <Badge variant={e.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {e.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right py-2">
                      <div className="inline-flex items-center gap-0.5">
                        {onNavigate && (
                          <Button
                            variant="ghost" size="icon" className="h-6 w-6"
                            title="Open detail"
                            onClick={() => onNavigate('entity-detail', { id: e.id })}
                          >
                            <ExternalLink size={12} />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6"
                          title="Edit entity"
                          onClick={() => setEditEntity({ ...e })}
                        >
                          <Pencil size={12} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}

            {groupedTrees.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                  {search ? 'No entities match your search' : 'No entities found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Edit / Create dialog */}
      {(editEntity || creating) && (
        <EntityFormDialog
          entity={editEntity}
          allEntities={entities || []}
          onClose={() => { setEditEntity(null); setCreating(false); }}
          onSaved={() => { setEditEntity(null); setCreating(false); refresh(); }}
        />
      )}
    </div>
  );
}

/* ───── Form dialog (create + edit) ───────────────────────────────────── */

interface EntityFormDialogProps {
  entity: Entity | null;            // null → create mode
  allEntities: Entity[];
  onClose: () => void;
  onSaved: () => void;
}

function EntityFormDialog({ entity, allEntities, onClose, onSaved }: EntityFormDialogProps) {
  const isEdit = !!entity;
  const [id, setId] = useState(entity?.id || '');
  const [name, setName] = useState(entity?.name || '');
  const [legalName, setLegalName] = useState(entity?.legal_name || '');
  const [jurisdiction, setJurisdiction] = useState(entity?.jurisdiction || '');
  const [regulator, setRegulator] = useState(entity?.regulator || '');
  const [licenceType, setLicenceType] = useState(entity?.licence_type || '');
  const [groupTag, setGroupTag] = useState(entity?.group_tag || 'wwg');
  const [parentEntity, setParentEntity] = useState(entity?.parent_entity || '');
  const [isActive, setIsActive] = useState(entity?.is_active ?? true);
  const [minCapital, setMinCapital] = useState(
    entity?.min_capital_eur != null ? String(entity.min_capital_eur) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // id must be a db-safe slug when creating new rows
  const idOk = !isEdit ? /^[a-z0-9_]+$/.test(id) : true;
  const canSave = !!id && !!name && !!groupTag && idOk;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('fin_auth_token');
      const payload: Record<string, any> = {
        id: id.trim(),
        name: name.trim(),
        legal_name: legalName.trim() || null,
        jurisdiction: jurisdiction.trim() || null,
        regulator: regulator.trim() || null,
        licence_type: licenceType.trim() || null,
        group_tag: groupTag,
        parent_entity: parentEntity || null,
        is_active: isActive,
        min_capital_eur: minCapital.trim() ? parseFloat(minCapital) : null,
      };
      const res = await fetch('/api/fin/entities', {
        method: isEdit ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} />
            {isEdit ? `Edit Entity: ${entity!.id}` : 'New Entity'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>ID</Label>
              <Input
                value={id}
                onChange={e => setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                placeholder="wwmkt"
                disabled={isEdit}
                className="font-mono text-xs"
              />
              {!isEdit && (
                <p className="text-[10px] text-muted-foreground">
                  Lowercase, digits, underscores only — cannot be changed later.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Group</Label>
              <Select value={groupTag} onValueChange={setGroupTag}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wwg">WWG Group</SelectItem>
                  <SelectItem value="vault">The Vault Group</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="WW Markets" />
          </div>

          <div className="space-y-1.5">
            <Label>Legal name</Label>
            <Input value={legalName} onChange={e => setLegalName(e.target.value)} placeholder="Wise Wolves Markets Ltd" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jurisdiction</Label>
              <Input value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} placeholder="CY" maxLength={3} />
            </div>
            <div className="space-y-1.5">
              <Label>Regulator</Label>
              <Input value={regulator} onChange={e => setRegulator(e.target.value)} placeholder="CySEC" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Licence type</Label>
              <Input value={licenceType} onChange={e => setLicenceType(e.target.value)} placeholder="CIF" />
            </div>
            <div className="space-y-1.5">
              <Label>Min capital (EUR)</Label>
              <Input
                value={minCapital}
                onChange={e => setMinCapital(e.target.value)}
                placeholder="750000"
                type="number" step="1000"
                className="font-mono"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Parent entity</Label>
            <Select
              value={parentEntity || '__none__'}
              onValueChange={v => setParentEntity(v === '__none__' ? '' : v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (root entity)</SelectItem>
                {allEntities
                  .filter(e => e.id !== (entity?.id || id))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} <span className="opacity-50 font-mono text-[10px] ml-1">{e.id}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {isEdit && (
            <div className="flex items-center justify-between border-t pt-3">
              <Label htmlFor="entity-is-active" className="text-sm">Active</Label>
              <Switch id="entity-is-active" checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}

          {error && (
            <div className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle size={12} /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || saving}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
